/**
 * AI 问照片 hover 快捷卡片
 * hover 入口按钮时弹出，点击「开始对话」打开面板，点击快捷问题打开面板并自动发送。
 */
import React, { useEffect, useRef } from 'react'
import {
  trackHoverCardClick,
  trackHoverCardExposure
} from '../utils/aiPhotoJunTrack'
import './AiQuickAskCard.scss'

/* ---------- inline SVG icons ---------- */
const IconChat = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M14.6666 2H1.33331V12H4.33331V13.6667L7.66665 12H14.6666V2Z"
      fill="url(#qc_chat_g1)"
      stroke="url(#qc_chat_g2)"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.66669 6.5V7.5"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 6.5V7.5"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.3333 6.5V7.5"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="qc_chat_g1"
        x1="1.333"
        y1="1.708"
        x2="8.443"
        y2="16.876"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#3357FF" />
        <stop offset="1" stopColor="#775BFD" />
      </linearGradient>
      <linearGradient
        id="qc_chat_g2"
        x1="1.333"
        y1="1.708"
        x2="8.443"
        y2="16.876"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#3357FF" />
        <stop offset="1" stopColor="#775BFD" />
      </linearGradient>
    </defs>
  </svg>
)

const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M12 8.003H4"
      stroke="#666"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 4L12 8L8 12"
      stroke="#666"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * 内置兜底快捷问题: 后端 preset_questions 未加载 / 为空 / 拉取失败时展示, 保证卡片不空。
 * 正常走后端下发的个性化问题 (见 AiQuickAskCardProps.questions)。
 */
export const QUICK_QUESTIONS = [
  '谁这周还没拍照',
  '本周谁拍照最多',
  '哪个工地拍最多',
  '导出本月明细 Excel'
] as const

/** 卡片最多展示几条快捷问题: 后端可能下发更多, UI 写死最多 5 条 */
const MAX_QUICK_QUESTIONS = 5

interface AiQuickAskCardProps {
  /** 「开始对话(自由提问)」回调: 打开面板, 不预填问题 */
  onStartChat: () => void
  /** 点击某条快捷问题: 打开面板并自动发送该问题 */
  onQuickAsk: (question: string) => void
  /**
   * 后端下发的快捷问题 (GET /web/xiaohei-claw/preset_questions, 按团队个性化)。
   * 非空时优先展示; 为空 / 拉取失败时回退到内置 QUICK_QUESTIONS。
   */
  questions?: string[]
  /**
   * 后端是否仍在拉取中。true 时只渲染「开始对话」、不出问题列表,
   * 避免先闪内置兜底再跳成后端动态问题 (danyang 反馈)。
   * 加载结束 (loading=false) 后: 有数据用 questions, 报错 / 空回退 QUICK_QUESTIONS。
   */
  loading?: boolean
}

const AiQuickAskCard: React.FC<AiQuickAskCardProps> = ({
  onStartChat,
  onQuickAsk,
  questions,
  loading
}) => {
  // 加载中: 不出任何问题 (避免先闪兜底再跳动态); 加载完: 有后端数据用后端的, 报错 / 空回退内置。
  // 后端可能返回多于 5 条, 截断到 MAX_QUICK_QUESTIONS。
  const source = questions && questions.length > 0 ? questions : QUICK_QUESTIONS
  const items: readonly string[] = loading
    ? []
    : source.slice(0, MAX_QUICK_QUESTIONS)

  // 埋点 workgroup_b4343_c8144_exposure: 卡片展示出内容(加载结束)时上报一次。
  // 卡片随 hover 挂载/卸载, ref 每次挂载重置 → "每次 hover 展示"各算一次曝光。
  const exposedRef = useRef(false)
  useEffect(() => {
    if (loading || exposedRef.current) return
    exposedRef.current = true
    trackHoverCardExposure(items)
  }, [loading, items])

  // 埋点 workgroup_b4343_c8144_click: 点「开始对话」/ 点某条预设问题各上报一次
  const handlePrimaryClick = () => {
    trackHoverCardClick({ clickItem: '开始对话', presets: items })
    onStartChat()
  }
  const handleQuestionClick = (question: string, indexFromZero: number) => {
    trackHoverCardClick({
      clickItem: '点击预设问题',
      presets: items,
      index: indexFromZero + 1,
      question
    })
    onQuickAsk(question)
  }

  return (
    <div className="ai-quick-ask-card">
      <div className="ai-quick-ask-card__header">一句话查团队拍照数据</div>
      <div
        className="ai-quick-ask-card__item ai-quick-ask-card__item--primary"
        onClick={handlePrimaryClick}
      >
        <IconChat />
        <span className="ai-quick-ask-card__text">开始对话（自由提问）</span>
        <IconArrow />
      </div>
      {items.length > 0 && (
        <>
          <div className="ai-quick-ask-card__divider" />
          {items.map((q, i) => (
            <div
              key={q}
              className="ai-quick-ask-card__item"
              onClick={() => handleQuestionClick(q, i)}
            >
              <span className="ai-quick-ask-card__text">{q}</span>
              <IconArrow />
            </div>
          ))}
        </>
      )}
    </div>
  )
}

export default AiQuickAskCard
